import React, { useEffect } from 'react';
import { logViolation } from '../api';

export default function ExamSecurity({ token, onViolation }) {
  useEffect(() => {
    const report = (type, meta) => {
      if (onViolation) onViolation(type, meta);
      // also inform backend
      if (token) logViolation(token, type, meta).catch(()=>{});
    };

    const blockEvent = (e) => { e.preventDefault(); return false; };

    const copyBlock = (e) => {
      e.preventDefault();
      report('copy-paste', { action: e.type }); // e.type will be 'copy', 'cut', or 'paste'
    };

    const keyHandler = (e) => {
      // block common shortcuts
      if (e.key === 'F12' || (e.ctrlKey && (e.key === 'p' || e.key === 's' || e.key === 'c' || e.key === 'v' || e.key === 'u'))) {
        e.preventDefault();
        report('blocked-shortcut', { key: e.key });
      }
    };

    const visibilityHandler = () => {
      if (document.hidden) report('tab-switch');
    };

    // Enhanced fullscreen detection with cross-browser support
    let wasFullscreen = false;
    
    const checkFullscreen = () => {
      const isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      
      // Only report if we transitioned from fullscreen to not fullscreen
      if (wasFullscreen && !isFullscreen) {
        report('exit-fullscreen');
      }
      
      wasFullscreen = isFullscreen;
    };

    const fullscreenHandler = () => {
      checkFullscreen();
    };

    // Check initial state
    checkFullscreen();
    
    // Also check periodically in case events are missed
    const fullscreenCheckInterval = setInterval(checkFullscreen, 1000);

    // DevTools detection (heuristic)
    let devtoolsOpen = false;
    const detectDevTools = () => {
      const threshold = 160;
      if (window.outerWidth - window.innerWidth > threshold || window.outerHeight - window.innerHeight > threshold) {
        if (!devtoolsOpen) { devtoolsOpen = true; report('devtools-open'); }
      } else {
        devtoolsOpen = false;
      }
    };

    // Multiple screen detection
    let lastScreenCount = 1;
    let multipleScreensDetected = false;
    let lastDetectionTime = 0;
    const MIN_DETECTION_INTERVAL = 5000; // Only report once every 5 seconds to avoid spam
    
    const detectMultipleScreens = async () => {
      try {
        const now = Date.now();
        
        // Method 1: Use Screen Details API (Chrome/Edge) - Most reliable
        if (window.getScreenDetails) {
          try {
            const screenDetails = await window.getScreenDetails();
            const screenCount = screenDetails.screens.length;
            
            if (screenCount > 1 && !multipleScreensDetected && (now - lastDetectionTime > MIN_DETECTION_INTERVAL)) {
              multipleScreensDetected = true;
              lastDetectionTime = now;
              report('multiple-screens', { 
                count: screenCount,
                method: 'Screen Details API',
                screens: screenDetails.screens.map(s => ({
                  width: s.width,
                  height: s.height,
                  left: s.left,
                  top: s.top
                }))
              });
            } else if (screenCount === 1) {
              multipleScreensDetected = false;
            }
            
            lastScreenCount = screenCount;
            return;
          } catch (err) {
            // Permission denied or API not available
          }
        }
        
        // Method 2: Use window.screen API (fallback) - More conservative heuristic
        // Check if window extends beyond primary screen bounds
        const screenWidth = window.screen.width;
        const screenHeight = window.screen.height;
        const screenX = window.screenX || window.screenLeft || 0;
        const screenY = window.screenY || window.screenTop || 0;
        
        // Check if window is positioned outside primary screen
        // This is a heuristic - if window is far outside screen bounds, likely multiple screens
        const windowWidth = window.outerWidth || window.innerWidth;
        const windowHeight = window.outerHeight || window.innerHeight;
        
        // More conservative thresholds to reduce false positives
        const extendsRight = (screenX + windowWidth) > (screenWidth + 200);
        const extendsLeft = screenX < -200;
        const extendsBottom = (screenY + windowHeight) > (screenHeight + 200);
        const extendsTop = screenY < -200;
        
        if ((extendsRight || extendsLeft || extendsBottom || extendsTop) && 
            !multipleScreensDetected && 
            (now - lastDetectionTime > MIN_DETECTION_INTERVAL)) {
          multipleScreensDetected = true;
          lastDetectionTime = now;
          report('multiple-screens', { 
            method: 'Window Position Heuristic',
            windowBounds: {
              left: screenX,
              top: screenY,
              width: windowWidth,
              height: windowHeight
            },
            screenBounds: {
              width: screenWidth,
              height: screenHeight
            }
          });
        } else if (!extendsRight && !extendsLeft && !extendsBottom && !extendsTop) {
          // Window is within primary screen bounds
          multipleScreensDetected = false;
        }
      } catch (error) {
      }
    };

    // Initial check
    detectMultipleScreens();
    
    // Check periodically for multiple screens (every 3 seconds)
    const screenCheckInterval = setInterval(detectMultipleScreens, 3000);
    
    // Also check on window move/resize
    const handleWindowMove = () => {
      detectMultipleScreens();
    };
    
    window.addEventListener('resize', handleWindowMove);
    // Note: 'move' event is not widely supported, so we rely on resize and periodic checks

    document.addEventListener('contextmenu', blockEvent);
    document.addEventListener('copy', copyBlock);
    document.addEventListener('cut', copyBlock);
    document.addEventListener('paste', copyBlock);
    window.addEventListener('keydown', keyHandler);
    document.addEventListener('visibilitychange', visibilityHandler);
    
    // Add fullscreen change listeners for all browser prefixes
    document.addEventListener('fullscreenchange', fullscreenHandler);
    document.addEventListener('webkitfullscreenchange', fullscreenHandler);
    document.addEventListener('mozfullscreenchange', fullscreenHandler);
    document.addEventListener('MSFullscreenChange', fullscreenHandler);
    
    const dv = setInterval(detectDevTools, 1000);

    return () => {
      document.removeEventListener('contextmenu', blockEvent);
      document.removeEventListener('copy', copyBlock);
      document.removeEventListener('cut', copyBlock);
      document.removeEventListener('paste', copyBlock);
      window.removeEventListener('keydown', keyHandler);
      document.removeEventListener('visibilitychange', visibilityHandler);
      document.removeEventListener('fullscreenchange', fullscreenHandler);
      document.removeEventListener('webkitfullscreenchange', fullscreenHandler);
      document.removeEventListener('mozfullscreenchange', fullscreenHandler);
      document.removeEventListener('MSFullscreenChange', fullscreenHandler);
      window.removeEventListener('resize', handleWindowMove);
      clearInterval(dv);
      clearInterval(fullscreenCheckInterval);
      clearInterval(screenCheckInterval);
    };
  }, [token, onViolation]);

  return null;
}
